import React, { useState, useCallback, memo } from 'react';
import { PendingApproval } from '../../services/approvals';
import './ImageCard.css';

interface ImageCardProps {
  approval: PendingApproval;
  selected: boolean;
  isProcessing?: boolean;
  disabled?: boolean;
  onSelect: () => void;
  onApprove: (platforms: string[]) => void;
  onReject: (reason: string) => void;
  onView: () => void;
}

// Memoize the component to prevent unnecessary re-renders
const ImageCard: React.FC<ImageCardProps> = memo(({
  approval,
  selected,
  isProcessing = false,
  disabled = false,
  onSelect,
  onApprove,
  onReject,
  onView,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Default platforms
  const defaultPlatforms = ['shutterstock', 'alamy', 'pond5', 'depositphotos'];

  // Get quality color
  const getQualityColor = (score?: number) => {
    if (!score) return 'var(--color-gray-400)';
    if (score >= 80) return 'var(--color-success)';
    if (score >= 60) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  // Format file size
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  // Handle quick approve - memoized to prevent recreation
  const handleQuickApprove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!disabled && !isProcessing) {
      onApprove(approval.selected_platforms || defaultPlatforms);
    }
  }, [disabled, isProcessing, onApprove, approval.selected_platforms, defaultPlatforms]);

  // Handle quick reject - memoized to prevent recreation
  const handleQuickReject = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!disabled && !isProcessing) {
      onReject('Quick rejection - Quality not sufficient');
    }
  }, [disabled, isProcessing, onReject]);

  return (
    <div className={`image-card ${selected ? 'selected' : ''} ${isProcessing ? 'processing' : ''} ${disabled ? 'disabled' : ''}`}>
      {/* Processing Overlay */}
      {isProcessing && (
        <div className="card-processing-overlay">
          <div className="processing-spinner"></div>
          <span>Processing...</span>
        </div>
      )}

      {/* Selection Checkbox */}
      <div className="card-select" onClick={(e) => { e.stopPropagation(); !disabled && !isProcessing && onSelect(); }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => {
            // Handled by parent onClick
          }}
          className="select-checkbox"
          disabled={disabled || isProcessing}
        />
      </div>

      {/* Image Container */}
      <div className="card-image" onClick={onView}>
        {!imageLoaded && !imageError && (
          <div className="image-skeleton">
            <div className="skeleton-spinner"></div>
          </div>
        )}

        {imageError ? (
          <div className="image-error">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <rect x="8" y="12" width="32" height="24" rx="2" stroke="currentColor" strokeWidth="2"/>
              <circle cx="16" cy="20" r="2" fill="currentColor"/>
              <path d="M8 28l8-8 8 8 12-12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>Failed to load</p>
          </div>
        ) : (
          <img
            src={`/api/images/thumbnail/${approval.image_id}` || approval.thumbnail_url || approval.preview_url || `/api/approvals/${approval.id}/preview`}
            alt={approval.title || 'Stock image'}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
            style={{ display: imageLoaded ? 'block' : 'none' }}
            loading="lazy"
          />
        )}

        {/* Quality Badge */}
        <div
          className="quality-badge"
          style={{ backgroundColor: getQualityColor(approval.quality_score) }}
        >
          {approval.quality_score || 'N/A'}
        </div>

        {/* Hover Overlay */}
        <div className="card-overlay">
          <button className="overlay-btn">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
            </svg>
            View Details
          </button>
        </div>
      </div>

      {/* Card Info */}
      <div className="card-info">
        <h3 className="card-title">
          {approval.title ||
           (approval.original_filename && !approval.original_filename.endsWith('.mp4')
             ? approval.original_filename
             : approval.original_filename?.replace('.mp4', '.jpg')) ||
           `Image ${approval.image_id}`}
        </h3>

        <div className="card-meta">
          <span className="meta-item">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
              <path d="M7 0a7 7 0 110 14A7 7 0 017 0zm0 1a6 6 0 100 12A6 6 0 007 1z"/>
              <path d="M7 3v4l2.5 1.5-.5.87L6 7.5V3h1z"/>
            </svg>
            {new Date(approval.created_at).toLocaleDateString()}
          </span>
          <span className="meta-item">
            {approval.resolution || 'Unknown res'}
          </span>
          <span className="meta-item">
            {formatFileSize(approval.file_size_bytes)}
          </span>
        </div>

        {/* Keywords Preview */}
        {approval.keywords && approval.keywords.length > 0 && (
          <div className="card-keywords">
            {approval.keywords.slice(0, 3).map((keyword, index) => (
              <span key={index} className="keyword-tag">
                {keyword}
              </span>
            ))}
            {approval.keywords.length > 3 && (
              <span className="keyword-more">+{approval.keywords.length - 3}</span>
            )}
          </div>
        )}
      </div>

      {/* Card Actions */}
      <div className="card-actions">
        <button
          className="action-btn approve-btn"
          onClick={handleQuickApprove}
          title="Quick Approve"
          disabled={disabled || isProcessing}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path fillRule="evenodd" d="M10.97 4.97a.75.75 0 011.07 1.05l-3.99 4.99a.75.75 0 01-1.08.02L4.324 8.384a.75.75 0 111.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 01.02-.022z"/>
          </svg>
          Approve
        </button>
        <button
          className="action-btn reject-btn"
          onClick={handleQuickReject}
          title="Quick Reject"
          disabled={disabled || isProcessing}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path fillRule="evenodd" d="M4.646 4.646a.5.5 0 01.708 0L8 7.293l2.646-2.647a.5.5 0 01.708.708L8.707 8l2.647 2.646a.5.5 0 01-.708.708L8 8.707l-2.646 2.647a.5.5 0 01-.708-.708L7.293 8 4.646 5.354a.5.5 0 010-.708z"/>
          </svg>
          Reject
        </button>
        <button
          className="action-btn view-btn"
          onClick={(e) => { e.stopPropagation(); onView(); }}
          title="View Details"
          disabled={disabled || isProcessing}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M6.75 0a.75.75 0 01.75.75V2h1V.75a.75.75 0 011.5 0V2h1.25c.966 0 1.75.784 1.75 1.75v10.5A1.75 1.75 0 0111.25 16h-6.5A1.75 1.75 0 013 14.25V3.75C3 2.784 3.784 2 4.75 2H6V.75A.75.75 0 016.75 0zm1.294 9.53l2.25-2.25a.75.75 0 111.06 1.06l-2.25 2.25a.75.75 0 01-1.06 0l-1-1a.75.75 0 011.06-1.06l.47.47.47-.47z"/>
          </svg>
        </button>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memo
  // Return true if props are equal (skip re-render), false if different (re-render)
  return (
    prevProps.approval.id === nextProps.approval.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.isProcessing === nextProps.isProcessing &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.approval.status === nextProps.approval.status
  );
});

ImageCard.displayName = 'ImageCard';

export default ImageCard;